import { defineLocalStore } from '@/utils/localStore'
import axios from '@/plugins/axios'
import {
  urlAcknowledgmentAssignmentByEntity,
  urlAcknowledgmentByEntity,
  urlAcknowledgmentAssignmentCreate,
  urlAcknowledgmentAssignmentDelete,
  urlAcknowledgmentAgree,
} from '@/utils/urls'
import { handleAsync } from 'best-modules/plugins'
import { computed, shallowRef } from 'vue'
import { useStore } from 'vuex'
import { fillDirectory } from '@/directory'
import {
  Acknowledgment,
  AcknowledgmentAssignment,
} from '@/utils/types/acknowledgment'
import { useCan } from '@/composables/useCan'

export const {
  useLocalStore: useAcknowledgment,
  initLocalStore: initAcknowledgment,
} = defineLocalStore(
  'acknowledgment',
  (payload: { entityTypeId: number; entityId: number }) => {
    const { entityTypeId, entityId } = payload
    const { can } = useCan()
    const store = useStore()
    const userId = computed<number>(() => store.state.user.userData?.id)

    const acknowledgmentList = shallowRef<Acknowledgment[]>([])
    const assignmentList = shallowRef<AcknowledgmentAssignment[]>([])

    const acknowledgmentAccess = computed<boolean>(() => {
      return acknowledgmentList.value.some(a => {
        return a.userId === userId.value
      })
    })

    const isAcknowledgmentAgreed = computed<boolean>(() => {
      if (acknowledgmentAccess.value) {
        return (
          acknowledgmentList.value.find(a => {
            return a.userId === userId.value
          })?.isAccept ?? false
        )
      } else {
        return false
      }
    })

    const getAcknowledgmentList = () => {
      return handleAsync('acknowledgmentList', () => {
        return axios
          .get(urlAcknowledgmentByEntity(entityTypeId, entityId))
          .then(res => {
            acknowledgmentList.value = res.data
          })
      })
    }
    const getAssignmentList = () => {
      return handleAsync('assignmentList', () => {
        return axios
          .get(urlAcknowledgmentAssignmentByEntity(entityTypeId, entityId))
          .then(res => {
            assignmentList.value = res.data
          })
      })
    }

    const createAssignment = (assigneeEntity: {
      entityTypeId: number
      entityId: number
    }) => {
      can('acknowledgments.create', () => {
        return handleAsync('createAssignment', () => {
          return axios
            .post(urlAcknowledgmentAssignmentCreate(), {
              entityTypeId,
              entityId,
              assigneeTypeId: assigneeEntity.entityTypeId === 6 ? 1 : 2,
              assigneeId: assigneeEntity.entityId,
            })
            .then(res => {
              assignmentList.value = [...assignmentList.value, res.data]
              getAcknowledgmentList()
            })
        })
      })
    }
    const removeAssignment = (assignmentId: number) => {
      can('acknowledgments.delete', () => {
        return handleAsync('removeAssignment', () => {
          return axios.delete(urlAcknowledgmentAssignmentDelete(assignmentId))
        }).then(() => {
          assignmentList.value = assignmentList.value.filter(
            a => a.id !== assignmentId
          )
          getAcknowledgmentList()
        })
      })
    }

    const acknowledgmentAgree = () => {
      return handleAsync('acknowledgmentAgree', () => {
        const acknowledgmentId = acknowledgmentList.value.find(
          a => a.userId === userId.value
        )?.id
        return axios
          .patch(urlAcknowledgmentAgree(), {
            acknowledgmentId,
          })
          .then(getAcknowledgmentList)
      })
    }

    const findAssignmentByEntity = (entityTypeId: number, entityId: number) => {
      return assignmentList.value.find(a => {
        switch (true) {
          case entityId !== a.assigneeId:
            return false
          case entityTypeId === 6 && a.assigneeTypeId === 2:
            return false
          case entityTypeId === 7 && a.assigneeTypeId === 1:
            return false
          default:
            return true
        }
      })
    }

    getAcknowledgmentList()
    getAssignmentList()
    fillDirectory({
      name: 'entityList',
      query: { filter: ['user', 'department'] },
      force: true,
    })

    return {
      acknowledgmentList,
      assignmentList,
      isAcknowledgmentAgreed,
      acknowledgmentAccess,
      getAcknowledgmentList,
      getAssignmentList,
      createAssignment,
      removeAssignment,
      acknowledgmentAgree,
      findAssignmentByEntity,
    }
  }
)
